import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { firestore } from "../firebaseConfig";
import { Order } from "../models";
import { useUserSession } from "./UserContext";
import { useWorkspace } from "./WorkspaceContext";

interface OrdersRealtimeState {
  orders: Order[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

const defaultState: OrdersRealtimeState = {
  orders: [],
  isLoading: false,
  isError: false,
  error: null,
};

const OrdersRealtimeContext = createContext<OrdersRealtimeState>(defaultState);

/**
 * Routes that must never start the workspace-wide order listener. The public
 * order form and the auth pages can render without a signed-in workspace user,
 * and a background listener there only produces permission errors.
 */
const PUBLIC_ROUTE_PREFIXES = [
  "/order/",
  "/shopify/",
  "/login",
  "/logout",
  "/signup",
  "/oauth/authorize",
  "/admin-mcp/oauth/authorize",
  "/load_workspace",
  "/reset_password",
];

/**
 * Single workspace-wide realtime order listener, shared with every consumer
 * through context.
 *
 * This replaces the `@react-query-firebase/firestore` subscription that used
 * to back `useGlobalOrders`. That library (an unmaintained dev prerelease)
 * resolves the initial React Query promise from global internal state, and
 * combined with the cache-key rotation on every order write it could leave the
 * query stuck in `isLoading` forever. A plain `onSnapshot` cannot hang: the
 * first snapshot — including an empty one — always flips `isLoading` to false,
 * and later writes arrive automatically without any cache invalidation.
 *
 * Keeping the listener in a provider (instead of inside the hook) is what
 * preserves the "single listener" cost optimization: every consumer of
 * `useGlobalOrders` shares this one subscription.
 */
export function OrdersRealtimeProvider({ children }: PropsWithChildren) {
  const { activeWorkspace } = useWorkspace();
  const { isAuthenticated } = useUserSession();
  const location = useLocation();

  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );
  const enabled = !!workspaceId && !isPublicRoute && isAuthenticated();

  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled) {
      setOrders([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);

    const unsubscribe = firestore
      .collection(Order.collectionName)
      .where("workspaceId", "==", workspaceId)
      .withConverter(Order.converter)
      .onSnapshot(
        (snapshot) => {
          if (!active) return;
          setOrders(snapshot.docs.map((doc) => doc.data()));
          setIsLoading(false);
        },
        (err) => {
          if (!active) return;
          console.error("Orders realtime listener failed:", err);
          setError(err);
          setIsLoading(false);
        },
      );

    return () => {
      active = false;
      unsubscribe();
    };
    // `enabled` folds in workspaceId/auth/route; workspaceId is listed so a
    // workspace switch tears down the old listener and starts a new one.
  }, [enabled, workspaceId]);

  // Memoize the context value: the provider re-renders on every route change
  // (useLocation), and without a stable value identity every useGlobalOrders
  // consumer would re-render on each navigation even when orders are
  // unchanged.
  const value = useMemo(
    () => ({ orders, isLoading, isError: !!error, error }),
    [orders, isLoading, error],
  );

  return (
    <OrdersRealtimeContext.Provider value={value}>
      {children}
    </OrdersRealtimeContext.Provider>
  );
}

export function useOrdersRealtime(): OrdersRealtimeState {
  return useContext(OrdersRealtimeContext);
}
